/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import { keyframes } from '@emotion/react'
import type { MouseEvent } from 'react'
import type { ColDef } from 'ag-grid-community'
import colors from '@sportninja/common/constants/appColors'
import { t } from '@sportninja/common/i18n'
import DataGrid from 'src/components/ts-components/DataGrid'
import {
  OPERATIONS_GRID_HEADER_HEIGHT,
  OPERATIONS_GRID_ROW_HEIGHT,
  operationsGridStyle,
} from '../operationsGridStyle'
import RosterGridSkeleton from './RosterGridSkeleton'
import { rosterGridSkeletonColumnCount } from './rosterGridSkeletonColumnCount'

const translate = t as (key: string, options?: unknown) => string

interface RosterGridProps<TRow extends { id: string }> {
  rows: TRow[]
  columns: ColDef<TRow>[]
  /** Rows with a save in flight, dimmed until the server answers. */
  pendingIds: Set<string>
  /**
   * Rows to flash green once — the Team screen marks a member it just saved so
   * the change is easy to find.
   */
  highlightedIds?: ReadonlySet<string>
  loading: boolean
  testId: string
  /** Shown when there are no rows — names what is missing. */
  emptyLabel?: string
}

/**
 * One roster table. Shared by Players, Spares and Team Officials so all three
 * carry the same theme, pending treatment and empty state.
 *
 * Rows are not clickable: unlike the division/season grids there is nothing to
 * drill into, and a stray click must never interrupt an inline edit.
 */
const RosterGrid = <TRow extends { id: string }>({
  rows,
  columns,
  pendingIds,
  highlightedIds,
  loading,
  testId,
  emptyLabel,
}: RosterGridProps<TRow>) => {
  'use memo'

  if (loading) {
    // Rows-in-flight replace ag-grid's own spinner-over-empty-grid overlay
    // with a skeleton shaped like the table about to arrive — see
    // RosterGridSkeleton. `testId` moves onto the skeleton itself rather than
    // staying on `DataGrid`, which does not mount while loading is true.
    return (
      <RosterGridSkeleton
        columnCount={rosterGridSkeletonColumnCount(columns)}
        testId={testId}
      />
    )
  }

  return (
    <div
      css={[operationsGridStyle, highlightStyle, copyableValueStyle]}
      onMouseDownCapture={keepTextSelection}
    >
      <DataGrid<TRow>
        rowData={rows}
        columnDefs={columns}
        getRowId={({ data }) => data.id}
        domLayout='autoHeight'
        stickyHorizontalScrollbar
        // The theme's minimum body height only exists to fit the empty-state
        // overlay; with rows it leaves dead space under a one- or two-row list.
        autoHeightMinBodyHeight={rows.length > 0 ? 0 : undefined}
        headerHeight={OPERATIONS_GRID_HEADER_HEIGHT}
        rowHeight={OPERATIONS_GRID_ROW_HEIGHT}
        rowClassRules={{
          'operations-row-pending': ({ data }) =>
            Boolean(data && pendingIds.has(data.id)),
          [HIGHLIGHTED_ROW_CLASS]: ({ data }) =>
            Boolean(data && highlightedIds?.has(data.id)),
        }}
        overlayNoRowsTemplate={
          emptyLabel ?? translate('DiscoverScreen:noResults')
        }
        data-testid={testId}
      />
    </div>
  )
}

/**
 * The grid focuses a cell on mousedown, and that focus clears a drag before
 * it can start. Name and email keep the event so the browser can select them.
 */
const keepTextSelection = (event: MouseEvent) => {
  const target = event.target
  if (!(target instanceof Element)) {
    return
  }
  const colId = target.closest('.ag-cell')?.getAttribute('col-id')
  if (colId === 'name' || colId === 'email') {
    event.stopPropagation()
  }
}

const HIGHLIGHTED_ROW_CLASS = 'roster-row-highlighted'

const highlightFade = keyframes`
  0%,
  60% {
    box-shadow: inset 0 0 0 9999px ${colors.SUCCESS_300}4D;
  }
  100% {
    box-shadow: inset 0 0 0 9999px transparent;
  }
`

const highlightStyle = css`
  .${HIGHLIGHTED_ROW_CLASS} {
    animation: ${highlightFade} 5s ease-out;
  }
`

/*
 * AG Grid marks the whole sheet unselectable. Name and email opt back in
 * here, on this grid only, so the user can drag across the value and copy it.
 * A click does not select the value for them. Other columns keep the default,
 * and no other DataGrid is affected.
 *
 * The rule is repeated on descendants because an ancestor `user-select: none`
 * is what the grid sets, and the value itself has to say `text`.
 */
const copyableValueStyle = css`
  .ag-cell[col-id='name'],
  .ag-cell[col-id='name'] *,
  .ag-cell[col-id='email'],
  .ag-cell[col-id='email'] * {
    -webkit-user-select: text;
    user-select: text;
  }

  .ag-cell[col-id='email'] {
    cursor: text;
  }
`

export default RosterGrid
